import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Clock, TrendingUp, Briefcase, Calendar, FileText, Download, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { format } from "date-fns";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { downloadFile } from "../lib/download";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIE_COLORS = ["#10B981", "#F59E0B", "#6366F1", "#EC4899", "#8B5CF6", "#06B6D4"];

export default function EmployeeDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [myProjects, setMyProjects] = useState([]);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [projectSearch, setProjectSearch] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projectEntries, setProjectEntries] = useState(null);
  const [entriesLoading, setEntriesLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateRange.start) params.set("start_date", dateRange.start);
      if (dateRange.end) params.set("end_date", dateRange.end);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const [dashRes, projRes] = await Promise.all([
        axios.get(`${API}/employee/dashboard${qs}`),
        axios.get(`${API}/employee/projects`),
      ]);
      setData(dashRes.data);
      setMyProjects(projRes.data.projects || []);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExportPdf = async () => {
    try {
      const params = dateRange.start && dateRange.end ? `?start_date=${dateRange.start}&end_date=${dateRange.end}` : "";
      await downloadFile(`${API}/export/pdf/dashboard${params}`);
      toast.success("PDF downloaded");
    } catch { toast.error("Failed to export PDF"); }
  };

  const fetchProjectEntries = async (projectId) => {
    if (selectedProjectId === projectId) {
      setSelectedProjectId(null);
      setProjectEntries(null);
      return;
    }
    setSelectedProjectId(projectId);
    setEntriesLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.set("start_date", dateRange.start);
      if (dateRange.end) params.set("end_date", dateRange.end);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/employee/project/${encodeURIComponent(projectId)}/entries${qs}`);
      setProjectEntries(res.data);
    } catch {
      toast.error("Failed to load project entries");
    } finally {
      setEntriesLoading(false);
    }
  };

  if (loading && !data) {
    return (<div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>);
  }

  // Show ALL projects in pie chart (billable + non-billable), sorted by hours desc
  const sortedProjects = [...myProjects].filter(p => (p.total_hours || 0) > 0).sort((a, b) => (b.total_hours || 0) - (a.total_hours || 0));
  const projectPieData = sortedProjects.slice(0, 6).map(p => ({ name: p.project_id.length > 25 ? p.project_id.substring(0, 22) + "..." : p.project_id, value: Math.round(p.total_hours * 10) / 10 }));
  const filteredProjects = myProjects.filter(p => !projectSearch.trim() || `${p.project_id} ${p.company} ${p.status}`.toLowerCase().includes(projectSearch.toLowerCase()));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employee-dashboard">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="secondary" className="bg-blue-100 text-blue-800">Employee</Badge>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">My Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back, {user?.name || "User"}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">From</Label>
              <Input type="date" value={dateRange.start} onChange={(e) => setDateRange(p => ({ ...p, start: e.target.value }))} className="h-9 w-36" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">To</Label>
              <Input type="date" value={dateRange.end} onChange={(e) => setDateRange(p => ({ ...p, end: e.target.value }))} className="h-9 w-36" />
            </div>
          </div>
          <Button variant="outline" className="gap-2 mt-5" onClick={handleExportPdf} data-testid="export-pdf-btn">
            <Download className="w-4 h-4" /> PDF
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="rounded-xl border border-border/80 shadow-[0_1px_0_rgba(16,24,40,0.04)] hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200">
          <CardContent className="pt-6">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Utilization</p>
            <p className="text-3xl font-bold text-foreground tabular-nums">{data?.utilization_rate || 0}%</p>
            <Progress value={data?.utilization_rate || 0} className="mt-3 h-2" />
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 shadow-[0_1px_0_rgba(16,24,40,0.04)] hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200">
          <CardContent className="pt-6">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Total Hours</p>
            <p className="text-3xl font-bold tabular-nums">{data?.total_hours || 0}</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 shadow-[0_1px_0_rgba(16,24,40,0.04)] hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200">
          <CardContent className="pt-6">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Billable</p>
            <p className="text-3xl font-bold text-emerald-600 tabular-nums">{data?.fee_hours || 0}h</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 shadow-[0_1px_0_rgba(16,24,40,0.04)] hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200">
          <CardContent className="pt-6">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Non-Billable</p>
            <p className="text-3xl font-bold text-amber-600 tabular-nums">{data?.non_fee_hours || 0}h</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 shadow-[0_1px_0_rgba(16,24,40,0.04)] hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200">
          <CardContent className="pt-6">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Projects</p>
            <p className="text-3xl font-bold tabular-nums">{data?.projects_count || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hours Trend */}
        {data?.daily_trend?.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base font-semibold">My Hours Trend</CardTitle></CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.daily_trend}>
                    <defs>
                      <linearGradient id="cfee" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.3} /><stop offset="95%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                      <linearGradient id="cnf" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} /><stop offset="95%" stopColor="#F59E0B" stopOpacity={0} /></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => { try { return format(new Date(v), "MMM d"); } catch { return v; } }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                    <Legend />
                    <Area type="monotone" dataKey="fee_earning" name="Billable" stroke="#10B981" fillOpacity={1} fill="url(#cfee)" />
                    <Area type="monotone" dataKey="non_fee_earning" name="Non-Billable" stroke="#F59E0B" fillOpacity={1} fill="url(#cnf)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Project Distribution */}
        {projectPieData.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base font-semibold">My Utilization</CardTitle></CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={projectPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value" label={({ percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                      {projectPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => [`${v}h`, ""]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* My Projects Explorer */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base font-semibold">My Projects</CardTitle>
              <CardDescription>{myProjects.length} projects you've worked on</CardDescription>
            </div>
            <Input placeholder="Search projects..." value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)} className="w-64" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Project</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Company</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">My Hours</th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Billable</th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Non-Billable</th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Entries</th>
                  <th className="py-2.5 px-1"></th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map(p => (
                  <tr key={p.project_id} className={`border-b border-border/50 hover:bg-muted/30 cursor-pointer transition-[background-color] duration-150 ${selectedProjectId === p.project_id ? "bg-primary/5" : ""}`}
                    onClick={() => fetchProjectEntries(p.project_id)}>
                    <td className="py-2.5 px-3 font-medium max-w-[200px] truncate">{p.project_id}</td>
                    <td className="py-2.5 px-3 text-muted-foreground">{p.company}</td>
                    <td className="py-2.5 px-3"><Badge variant="secondary" className="text-xs">{p.status || "N/A"}</Badge></td>
                    <td className="py-2.5 px-3 text-right tabular-nums font-medium">{p.total_hours}h</td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-emerald-600">{p.fee_hours}h</td>
                    <td className="py-2.5 px-3 text-right tabular-nums text-amber-600">{p.non_fee_hours}h</td>
                    <td className="py-2.5 px-3 text-right tabular-nums">{p.entries_count}</td>
                    <td className="py-2.5 px-1"><ChevronRight className="w-4 h-4 text-muted-foreground" /></td>
                  </tr>
                ))}
                {filteredProjects.length === 0 && (
                  <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">No projects found. Your name must match the Personnel field in uploaded timesheets.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Project Entries Drill-down */}
          {entriesLoading && (
            <div className="flex items-center justify-center py-6 mt-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>
          )}
          {selectedProjectId && projectEntries && !entriesLoading && (
            <div className="mt-4 border border-primary/20 rounded-lg overflow-hidden animate-fade-in" data-testid="employee-project-entries-drilldown">
              <div className="p-3 bg-primary/5 border-b border-primary/20 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{projectEntries.personnel} — Entries for {selectedProjectId}</p>
                  <p className="text-xs text-muted-foreground">
                    {projectEntries.total_hours}h total · {projectEntries.fee_hours}h billable · {projectEntries.entries_count} entries
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedProjectId(null); setProjectEntries(null); }}>Close</Button>
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background">
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Date</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Description</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">Hours</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(projectEntries.entries || []).map((e, i) => (
                      <tr key={`${e.date}-${i}`} className="border-b border-border/50 hover:bg-muted/20">
                        <td className="py-1.5 px-3 text-muted-foreground whitespace-nowrap">{e.date}</td>
                        <td className="py-1.5 px-3 max-w-[300px] truncate" title={e.description}>{e.description || "-"}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums font-medium">{e.hours}</td>
                        <td className="py-1.5 px-3">
                          <Badge className={`text-[10px] px-1.5 py-0 ${(e.fee_earning || "").includes("Non-Fee") ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                            {(e.fee_earning || "").includes("Non-Fee") ? "Non-Fee" : "Billable"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                    {(projectEntries.entries || []).length === 0 && (
                      <tr><td colSpan={4} className="py-4 text-center text-muted-foreground">No entries found for this date range</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Entries */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Recent Entries</CardTitle>
          <CardDescription>{data?.entries_count || 0} total entries</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Project</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Hours</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recent_entries || []).map((entry, i) => (
                  <tr key={entry.id || i} className="border-b border-border/50 hover:bg-muted/30 transition-[background-color] duration-150">
                    <td className="py-2.5 px-3 text-muted-foreground whitespace-nowrap">{entry.date}</td>
                    <td className="py-2.5 px-3 font-medium">{entry.company || entry.project_id}</td>
                    <td className="py-2.5 px-3 text-muted-foreground max-w-[300px] truncate">{entry.description}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums font-medium">{entry.hours}</td>
                    <td className="py-2.5 px-3">
                      <Badge className={entry.fee_earning?.includes("Non-Fee") ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}>
                        {entry.fee_earning?.includes("Non-Fee") ? "Non-Fee" : "Billable"}
                      </Badge>
                    </td>
                  </tr>
                ))}
                {(!data?.recent_entries || data.recent_entries.length === 0) && (
                  <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">No entries found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
